import React, { useState } from 'react';
import './PromotionPage.css';
import { AiFillHome } from 'react-icons/ai';
import { MdRestaurantMenu } from 'react-icons/md';
import { FaMoneyBillWave, FaUserFriends, FaGift } from 'react-icons/fa';
import { GiKnifeFork } from 'react-icons/gi';

const navItems = [
  'Home',
  'Product',
  'Sales',
  'Members',
  'Promotion',
  'Restaurant',
];

const navIcons = {
  Home: <AiFillHome className="nav-icon" />,
  Product: <MdRestaurantMenu className="nav-icon" />,
  Sales: <FaMoneyBillWave className="nav-icon" />,
  Members: <FaUserFriends className="nav-icon" />,
  Promotion: <FaGift className="nav-icon" />,
  Restaurant: <GiKnifeFork className="nav-icon" />,
};

const promotionTypes = [
  'all',
  'percentage discount',
  'bundle',
  'bulk discount',
  'loyalty reward',
];
const statusTypes = [
  'all',
  'active',
  'scheduled',
  'expired',
];

const initialPromotions = [
  {
    id: 1,
    name: 'Summer Discount',
    type: 'percentage discount',
    discount: '20%',
    validity: '2024-06-01 ~ 2024-06-30',
    usage: '120/200',
    status: 'active',
  },
  {
    id: 2,
    name: 'Couple Package',
    type: 'bundle',
    discount: '2 people for $88',
    validity: '2024-06-10 ~ 2024-07-10',
    usage: 'unlimited',
    status: 'scheduled',
  },
  {
    id: 3,
    name: 'Points for Drinks',
    type: 'loyalty reward',
    discount: '1000 points for free drink',
    validity: '2024-05-01 ~ 2024-12-31',
    usage: '56/100',
    status: 'active',
  },
  {
    id: 4,
    name: 'Bulk Order Discount',
    type: 'bulk discount',
    discount: '10% off for 5+ items',
    validity: '2024-04-01 ~ 2024-06-15',
    usage: '200/200',
    status: 'expired',
  },
  {
    id: 5,
    name: 'New Member Special',
    type: 'percentage discount',
    discount: '20% off first order',
    validity: '2024-06-01 ~ 2024-12-31',
    usage: 'unlimited',
    status: 'active',
  },
  {
    id: 6,
    name: 'Weekend Special',
    type: 'percentage discount',
    discount: '15% off',
    validity: '2024-06-01 ~ 2024-08-31',
    usage: '45/100',
    status: 'active',
  },
  {
    id: 7,
    name: 'Family Bundle',
    type: 'bundle',
    discount: '4 people for $120',
    validity: '2024-06-15 ~ 2024-09-15',
    usage: 'unlimited',
    status: 'scheduled',
  },
  {
    id: 8,
    name: 'Lunch Special',
    type: 'percentage discount',
    discount: '25% off lunch',
    validity: '2024-05-01 ~ 2024-07-31',
    usage: '78/150',
    status: 'active',
  },
  {
    id: 9,
    name: 'Birthday Reward',
    type: 'loyalty reward',
    discount: 'Free dessert on birthday',
    validity: '2024-01-01 ~ 2024-12-31',
    usage: '12/50',
    status: 'active',
  },
  {
    id: 10,
    name: 'Student Discount',
    type: 'percentage discount',
    discount: '10% off with student ID',
    validity: '2024-03-01 ~ 2024-12-31',
    usage: 'unlimited',
    status: 'active',
  },
  {
    id: 11,
    name: 'Holiday Special',
    type: 'bundle',
    discount: 'Festival package $150',
    validity: '2024-12-01 ~ 2024-12-31',
    usage: 'unlimited',
    status: 'scheduled',
  },
  {
    id: 12,
    name: 'Early Bird Special',
    type: 'percentage discount',
    discount: '30% off before 6 PM',
    validity: '2024-04-01 ~ 2024-06-30',
    usage: '89/200',
    status: 'expired',
  },
  {
    id: 13,
    name: 'VIP Member Exclusive',
    type: 'loyalty reward',
    discount: 'Exclusive menu access',
    validity: '2024-01-01 ~ 2024-12-31',
    usage: 'unlimited',
    status: 'active',
  },
  {
    id: 14,
    name: 'Group Discount',
    type: 'bulk discount',
    discount: '15% off for 8+ people',
    validity: '2024-05-15 ~ 2024-08-15',
    usage: '23/50',
    status: 'active',
  },
  {
    id: 15,
    name: 'Seasonal Promotion',
    type: 'percentage discount',
    discount: 'Spring special 18% off',
    validity: '2024-03-01 ~ 2024-05-31',
    usage: '156/300',
    status: 'expired',
  }
];

function PromotionPage({ onNavigate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [promotions, setPromotions] = useState(initialPromotions);
  const [currentPage, setCurrentPage] = useState(1);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [editForm, setEditForm] = useState({});

  const itemsPerPage = 10;

  // Card data
  const activeCount = promotions.filter(p => p.status === 'active').length;
  const redemptionsToday = 32;
  const redemptionsTotal = 560;

  // Filter and search
  const filteredPromotions = promotions.filter(p => {
    const matchType = typeFilter === 'all' || p.type === typeFilter;
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchSearch =
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.discount.toLowerCase().includes(searchTerm.toLowerCase());
    return matchType && matchStatus && matchSearch;
  });

  const paginatedPromotions = filteredPromotions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleNavClick = (item) => {
    if (onNavigate) {
      onNavigate(item);
    }
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setEditForm({ ...promotion });
    setEditModalOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleEditSave = () => {
    setPromotions(promotions.map(p => p.id === editingPromotion.id ? { ...editForm } : p));
    setEditModalOpen(false);
    setEditingPromotion(null);
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingPromotion(null);
  };

  const handleRemove = (id) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      setPromotions(promotions.filter(p => p.id !== id));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'scheduled': return 'scheduled';
      case 'expired': return 'expired';
      default: return '';
    }
  };

  // Export CSV function
  const exportToCSV = () => {
    const headers = ['Promotion Name', 'Type', 'Discount', 'Validity Period', 'Usage', 'Status'];
    const csvContent = [
      headers.join(','),
      ...filteredPromotions.map(p => [
        p.name,
        p.type,
        p.discount,
        p.validity,
        p.usage,
        p.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'promotions.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="promotionpage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li
              key={item}
              className={item === 'Promotion' ? 'active' : ''}
              onClick={() => handleNavClick(item)}
            >
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>
      <main className="promotion-content">
        <div className="promotion-header">
          <div className="promotion-title">Promotion & Rewards</div>
        </div>
        <div className="promotion-cards">
          <div className="promotion-card">
            <div className="card-title">Active Promotions</div>
            <div className="card-value">{activeCount}</div>
          </div>
          <div className="promotion-card">
            <div className="card-title">Redemptions Today</div>
            <div className="card-value">{redemptionsToday}</div>
          </div>
          <div className="promotion-card">
            <div className="card-title">Redemptions Total</div>
            <div className="card-value">{redemptionsTotal}</div>
          </div>
        </div>
        <div className="promotion-table-container">
          <div className="table-header">
            <h2>Manage Promotions</h2>
            <div className="table-controls">
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search promotion name, type or discount..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filter-container">
                <select
                  className="filter-select"
                  value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value)}
                >
                  {promotionTypes.map(type => (
                    <option key={type} value={type}>{type === 'all' ? 'All Types' : type}</option>
                  ))}
                </select>
                <select
                  className="filter-select"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  {statusTypes.map(status => (
                    <option key={status} value={status}>{status === 'all' ? 'All Status' : status}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <table className="promotion-table">
            <thead>
              <tr>
                <th>Promotion Name</th>
                <th>Type</th>
                <th>Discount</th>
                <th>Validity Period</th>
                <th>Usage</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPromotions.map(p => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.type}</td>
                  <td>{p.discount}</td>
                  <td>{p.validity}</td>
                  <td>{p.usage}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(p.status)}`}>{p.status}</span>
                  </td>
                  <td className="actions">
                    <button className="edit-btn" onClick={() => handleEdit(p)}>Edit</button>
                    <button className="remove-btn" onClick={() => handleRemove(p.id)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-footer">
            <button className="export-csv-btn" onClick={exportToCSV}>
              Export CSV
            </button>
            <div className="pagination">
              <button 
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {Math.ceil(filteredPromotions.length / itemsPerPage)}
              </span>
              <button 
                className="pagination-btn"
                disabled={currentPage >= Math.ceil(filteredPromotions.length / itemsPerPage)}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </main>
      {/* 编辑模态框 */}
      {editModalOpen && (
        <div className="modal-overlay" onClick={handleEditCancel}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Promotion</h2>
              <button className="close-btn" onClick={handleEditCancel}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Promotion Name</label>
                <input type="text" value={editForm.name || ''} onChange={e => handleEditFormChange('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Type</label>
                <input type="text" value={editForm.type || ''} onChange={e => handleEditFormChange('type', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Discount</label>
                <input type="text" value={editForm.discount || ''} onChange={e => handleEditFormChange('discount', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Validity</label>
                <input type="text" value={editForm.validity || ''} onChange={e => handleEditFormChange('validity', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Usage</label>
                <input type="text" value={editForm.usage || ''} onChange={e => handleEditFormChange('usage', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={editForm.status || ''} onChange={e => handleEditFormChange('status', e.target.value)}>
                  <option value="active">active</option>
                  <option value="scheduled">scheduled</option>
                  <option value="expired">expired</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={handleEditSave}>Save</button>
              <button className="cancel-btn" onClick={handleEditCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromotionPage; 